import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { clsx } from 'clsx';
import { $toggleLink, TOGGLE_LINK_COMMAND, LinkNode } from '@lexical/link';
import { useEffect, useRef, useState } from 'react';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  mergeRegister,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';

import type { ElementNode, BaseSelection } from 'lexical';
import type { addLinkFormMethods } from './AddLinkForm/AddLinkFrom';
import type { linkFormMethods } from './LinkContextMenu/LinkContextMenu';

import { AddLinkForm } from './AddLinkForm/AddLinkFrom';
import { ensureHttps } from './helpers';
import { LinkContextMenu } from './LinkContextMenu/LinkContextMenu';

import { useFlyout } from 'components/RteEditor/hooks/useFlyout';
import { Icon } from 'components';
import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

const PREVENT_OPEN_DELAY = 200;

export interface LinkType {
  active: boolean;
  url: string;
  linktext: string;
}

type Position = {
  top: number;
  left: number;
};

interface LinkPluginProps {
  parentScrollPosition: number;
}

export const LinkPlugin = ({ parentScrollPosition }: LinkPluginProps) => {
  const t = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { flyout, showFlyout, hideFlyout, isVisible } = useFlyout();
  const addLinkForm = useRef<addLinkFormMethods>(undefined);
  const preventOpen = useRef(false);
  const previousLinkText = useRef('');
  const selectedText = useRef('');
  const [isLink, setIsLink] = useState<LinkType>({ active: false, url: '', linktext: '' });
  const isVisibleRef = useRef(isVisible);
  const storedSelection = useRef<BaseSelection | null>(null);
  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const editLinkForm = useRef<linkFormMethods>(undefined);

  const storeCursorPosition = () => {
    editor.update(() => {
      const selection = $getSelection();
      storedSelection.current = selection;
    });
  };

  const rePositionCursor = () => {
    editor.update(() => {
      const currentSelection = storedSelection.current;

      if (currentSelection && $isRangeSelection(currentSelection)) {
        const anchorNode = currentSelection.anchor.getNode();
        const focusNode = currentSelection.focus.getNode();

        if (anchorNode && focusNode && anchorNode.isAttached() && focusNode.isAttached()) {
          const anchorLength = anchorNode.getTextContentSize();
          const focusLength = focusNode.getTextContentSize();

          if (currentSelection.anchor.offset <= anchorLength && currentSelection.focus.offset <= focusLength) {
            $setSelection(currentSelection);
            return;
          }
        }
      }

      editor.getRootElement()?.focus();
    });
  };

  const openContextMenu = (url: string, position: Position) => {
    showFlyout({
      position,
      children: (
        <LinkContextMenu
          url={url}
          onSave={(url) => insertLink(url, '')}
          onDelete={() => removeLink()}
          onClose={closeContextMenu}
          ref={editLinkForm}
        />
      ),
    });
  };

  const closeContextMenu = () => {
    editLinkForm.current?.clearForm();
    hideFlyout();
    rePositionCursor();
  };

  const openFlyout = () => {
    const rect = linkButtonRef.current?.getBoundingClientRect();
    if (!preventOpen.current && rect) {
      storeCursorPosition();
      showFlyout({ position: { top: rect.bottom, left: rect.left }, ...flyoutOptions });
      setTimeout(() => addLinkForm.current?.setDefaults('', selectedText.current), 0);
    }
  };

  const closeFlyout = () => {
    hideFlyout();
    rePositionCursor();
  };

  const insertLink = (url: string, linktext: string) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const linkNode = $getNearestNodeOfType(anchorNode, LinkNode);

        if (linktext && linktext !== '') {
          if (linkNode) {
            const newTextNode = $createTextNode(linktext);
            linkNode.replace(newTextNode);
            newTextNode.select();
          }
          if (linktext !== previousLinkText.current && previousLinkText.current === '') {
            const { anchor, focus } = selection;
            selection.insertText(linktext);
            anchor.offset -= linktext.length;
            focus.offset = anchor.offset + linktext.length;
          }
        }
        $toggleLink(ensureHttps(url), { target: '_blank' });
      }

      closeFlyout();
    });
  };

  const removeLink = (): void => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    closeContextMenu();
  };

  const flyoutOptions = {
    children: (
      <AddLinkForm
        isLink={isLink}
        onCloseHandler={closeFlyout}
        onSaveHandler={(url, linktext) => insertLink(url, linktext)}
        ref={addLinkForm}
      />
    ),
  };

  useEffect(() => {
    if (!isVisible) {
      addLinkForm.current?.clearForm();
      previousLinkText.current = selectedText.current = '';
      setTimeout(() => (preventOpen.current = false), PREVENT_OPEN_DELAY);
    } else {
      preventOpen.current = true;
      addLinkForm.current?.focusFirstField();
    }
  }, [addLinkForm, isVisible]);

  useEffect(() => {
    const handleSelectionClickOrChange = (selection: BaseSelection | null) => {
      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const anchorNode = selection.anchor.getNode() as ElementNode;
        const node = $getNearestNodeOfType(anchorNode, LinkNode);

        if (node) {
          const domNode = editor.getElementByKey(selection.anchor.getNode().getKey());
          const position = domNode
            ? { top: domNode.getBoundingClientRect().bottom, left: domNode.getBoundingClientRect().left }
            : { top: 0, left: 0 };
          openContextMenu(node.getURL(), position);
          return true;
        }
      }
      return false;
    };

    const unregisterSelect = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        selectedText.current = selection?.getTextContent() ?? '';
        handleSelectionClickOrChange(selection);
        return false;
      },
      0
    );

    const unregisterClick = editor.registerCommand(
      CLICK_COMMAND,
      (event) => {
        const selection = $getSelection();
        storeCursorPosition();
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        handleSelectionClickOrChange(selection);
        return false;
      },
      0
    );

    return () => {
      unregisterClick();
      unregisterSelect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    const removeKeyboardShortcut = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.key === 'U') {
          const selection = $getSelection();
          storeCursorPosition();

          if (!$isRangeSelection(selection)) {
            return false;
          }

          const anchorNode = selection.anchor.getNode() as ElementNode;
          const node = $getNearestNodeOfType(anchorNode, LinkNode);

          const openFlyoutWithDefaults = (url = '', linkText = '') => {
            openFlyout();
            setTimeout(() => addLinkForm.current?.setDefaults(url, linkText), 0);
          };

          if (node) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            previousLinkText.current = node.getTextContent();
            openFlyoutWithDefaults(node.getURL(), node.getTextContent());

            return true;
          }

          const selectedTextContent = selection.getTextContent();
          if (selectedTextContent !== '') {
            selectedText.current = selectedTextContent;
            openFlyoutWithDefaults('', selectedText.current);

            return true;
          }

          openFlyout();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    const removeStatusListener = editor.registerUpdateListener(({ editorState }) => {
      setIsLink(
        editorState.read(() => {
          const selection = $getSelection();
          storeCursorPosition();

          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode() as ElementNode;
            const node = $getNearestNodeOfType(anchorNode, LinkNode);

            if (node) {
              return {
                active: true,
                url: node.getURL(),
                linktext: node.getTextContent(),
              };
            }
          }

          return {
            active: false,
            url: '',
            linktext: '',
          };
        })
      );
    });

    return () => {
      removeKeyboardShortcut();
      removeStatusListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
    if (!isVisible) editLinkForm.current?.clearForm();
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      closeFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentScrollPosition]);

  useEffect(() => {
    if (!isLink.active) {
      hideFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLink]);

  const handleOnScroll = () => {
    hideFlyout();
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          if (event.key === 'Escape' && isVisibleRef.current) {
            event.preventDefault();
            closeFlyout();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('scroll', handleOnScroll);
        }

        if (rootElement) {
          rootElement.addEventListener('scroll', handleOnScroll);
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return (
    <div className={styles['editor__toolbar-button-group']}>
      <div className={styles['editor__toolbar-button-flyoutwrapper']}>
        <button
          onClick={openFlyout}
          title={t.links.linkTooltip}
          className={clsx(
            styles['editor__toolbar-button'],
            styles[`editor__toolbar-button--link`],
            isLink.active && styles['editor__toolbar-button--active']
          )}
          aria-label={t.aria.labels.link}
          aria-keyshortcuts={t.aria.keyshortcuts.link}
          type="button"
          ref={linkButtonRef}
        >
          <Icon name="Link" size={20} />
        </button>
        {flyout}
      </div>
    </div>
  );
};
